import { Resources } from 'constants/resources';
import { Role } from 'constants/roles';

import { useQuery } from '@tanstack/react-query';
import { getUserInfo } from 'config/requests';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { ProviderAccountsManagerField } from 'pages/Finance/ProviderAccounts/components/ProviderAccountsManagerField';
import { ProviderAccountsMembers } from 'pages/Finance/ProviderAccounts/components/ProviderAccountsMembers/ProviderAccountsMembers';
import { useProviderAccountsAccess } from 'pages/Finance/ProviderAccounts/hooks/useProviderAccountsAccess';
import { RecordContextProvider } from 'react-admin';
import { AvatarStackAdd } from 'shared/mui/Avatars';
import { Box } from 'shared/mui/Box';
import { CreateButton } from 'shared/mui/Buttons';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowButton,
} from 'shared/mui/ContextMenu';
import { DeleteDialog } from 'shared/mui/Dialogs';
import { Tooltip } from 'shared/mui/Tooltip';
import { Typography } from 'shared/mui/Typography';
import { TypographyBold } from 'shared/mui/Typography/TypographyBold';
import { EmbeddedFunctionField, FunctionField, TextField } from 'shared/react-admin/Fields';
import { ListPage } from 'shared/react-admin/Pages';
import { CompanyShowDialog } from 'shared/widgets/CompanyShowDialog/CompanyShowDialog';
import { ProviderShowDialog } from 'shared/widgets/ProviderShowDialog/ProviderShowDialog';
import { WorkTeamsShowDialog } from 'shared/widgets/WorkTeamsShowDialog/WorkTeamsShowDialog';
import { Company } from 'types/embedded/company';
import { Permission } from 'types/embedded/permission';
import { Provider } from 'types/embedded/provider';
import { ProviderAccount } from 'types/embedded/providerAccount';
import { WorkTeam } from 'types/embedded/workTeam';
import { getId } from 'utils';

import { StyledDatagrid, StyledSpanField } from './ProviderAccountsList.styled';
import { ProviderAccountsListBulkActions } from './ProviderAccountsListBulkActions';
import { ProviderAccountsListFilter } from './ProviderAccountsListFilter/ProviderAccountsListFilter';
import { ProviderAccountsMerchantsField } from '../components/ProviderAccountsMerchantsField';
import { WorkTeamAddDialog } from './WorkTeamAddDialog/WorkTeamAddDialog';
import { WorkTeamUsersAddDialog } from './WorkTeamUsersAddDialog/WorkTeamUsersAddDialog';

export const ProviderAccountsList = () => {
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: getUserInfo });
  const isEditMembersDisabled = user?.roles.some((element) =>
    [Role.Finance, Role.Payment].includes(element),
  );

  const { hasResourcePermissions, hasResourceAccess, isHaveRole } = usePermissions();
  const { hasActionsAccess } = useProviderAccountsAccess();
  const canBulkAddMembers = isHaveRole([Role.Admin, Role.Tech, Role.FinanceLead]);
  const canCreate =
    hasActionsAccess &&
    hasResourcePermissions(Resources.ProviderAccounts, [Permission.Full, Permission.Create]);
  const canDelete =
    hasActionsAccess &&
    hasResourcePermissions(Resources.ProviderAccounts, [Permission.Full, Permission.Delete]);

  const companyModal = useRecordModal<Partial<Company>>();
  const deleteProviderModal = useRecordModal<ProviderAccount>();
  const showProviderModal = useRecordModal<Partial<Provider>>();
  const addWorkTeamModal = useRecordModal<{ providerAccount: ProviderAccount }>();
  const showWorkTeamModal = useRecordModal<WorkTeam>();
  const addWorkTeamUsersModal = useRecordModal<ProviderAccount>();

  return (
    <ListPage
      actions={[canCreate && <CreateButton key="create-provider-account" />]}
      empty={false}
      headerTitle={{
        titleText: 'Настройки доступов',
        tooltipText:
          'Учетная запись мерчанта в системе провайдера. Выдается мерчанту провайдером и обычно представляет собой URL для входа в кабинет, логин и пароль.',
      }}
    >
      <ProviderAccountsListFilter />
      <StyledDatagrid bulkActionButtons={canBulkAddMembers && <ProviderAccountsListBulkActions />}>
        <TextField label="ID" sortBy="id" source="originId" />
        <EmbeddedFunctionField
          cellClassName="column-company"
          label="Компания"
          render={(value: Company) => {
            return (
              <StyledSpanField onClick={() => companyModal.handleOpenRecordModal(value)}>
                {value.name}
              </StyledSpanField>
            );
          }}
          sortBy="company.name"
          sourceField="company"
        />
        <ProviderAccountsMerchantsField
          cellClassName="column-merchants"
          label="Мерчанты"
          sourceField="merchants"
        />
        {hasResourceAccess(Resources.Providers) && (
          <EmbeddedFunctionField
            cellClassName="column-provider"
            label="Провайдер"
            render={(value: Partial<Provider>) => {
              return (
                <StyledSpanField onClick={() => showProviderModal.handleOpenRecordModal(value)}>
                  {value.name}
                </StyledSpanField>
              );
            }}
            sortBy="provider.name"
            sourceField="provider"
          />
        )}
        {hasResourcePermissions(Resources.WorkTeams, [Permission.Full, Permission.Partial]) && (
          <FunctionField
            label="Рабочая группа"
            render={(providerAccount: ProviderAccount) => {
              if (!providerAccount.workTeam) {
                return (
                  <Tooltip arrow placement="bottom" title="Назначить группу">
                    <Box sx={{ width: 'fit-content' }}>
                      <AvatarStackAdd
                        onClick={() => addWorkTeamModal.handleOpenRecordModal({ providerAccount })}
                      />
                    </Box>
                  </Tooltip>
                );
              }

              return (
                <EmbeddedFunctionField
                  render={(value) => {
                    return (
                      <StyledSpanField
                        onClick={() => showWorkTeamModal.handleOpenRecordModal(value)}
                      >
                        {value.name}
                      </StyledSpanField>
                    );
                  }}
                  sourceField="workTeam"
                />
              );
            }}
            source="workTeam"
          />
        )}
        <EmbeddedFunctionField
          cellClassName="column-workTeamLead"
          label="Руководитель группы"
          render={(value: WorkTeam) => {
            return (
              <RecordContextProvider value={value}>
                <ProviderAccountsManagerField />
              </RecordContextProvider>
            );
          }}
          sourceField="workTeam"
        />
        <FunctionField
          cellClassName="column-users"
          label="Ответственные"
          render={(record: ProviderAccount) => {
            return (
              <ProviderAccountsMembers
                addSlot={
                  !isEditMembersDisabled && (
                    <AvatarStackAdd
                      onClick={() => addWorkTeamUsersModal.handleOpenRecordModal(record)}
                    />
                  )
                }
                members={record?.members}
              />
            );
          }}
        />
        <ContextMenu
          render={(record: ProviderAccount) => [
            hasResourceAccess(Resources.UserActions) && (
              <ContextMenuEntityUserActionsButton
                entityName="provider_account"
                key="user-action-button"
              />
            ),
            <ContextMenuShowButton key="show-button" />,
            hasResourcePermissions(Resources.ProviderAccounts, [
              Permission.Full,
              Permission.Update,
            ]) && <ContextMenuEditButton key="edit-button" />,
            canDelete && (
              <ContextMenuDeleteButton
                key="delete-button"
                onClick={() => deleteProviderModal.handleOpenRecordModal(record)}
              />
            ),
          ]}
        />
      </StyledDatagrid>
      <DeleteDialog
        details={
          <Typography>
            Настройка доступа{' '}
            <TypographyBold fontSize={16} text={`ID ${getId(deleteProviderModal.record?.id)}`} />{' '}
            будет удалена без возможности восстановления.
          </Typography>
        }
        onClose={deleteProviderModal.handleCloseRecordModal}
        open={deleteProviderModal.openRecordModal}
        record={deleteProviderModal.record}
        title="Удаление настройки доступа"
      />
      {companyModal.record && (
        <CompanyShowDialog
          company={companyModal.record}
          onClose={companyModal.handleCloseRecordModal}
          open={companyModal.openRecordModal}
        />
      )}
      {showProviderModal.record && (
        <ProviderShowDialog
          onClose={showProviderModal.handleCloseRecordModal}
          open={showProviderModal.openRecordModal}
          provider={showProviderModal.record}
        />
      )}
      {addWorkTeamModal.record && (
        <WorkTeamAddDialog
          onClose={addWorkTeamModal.handleCloseRecordModal}
          open={addWorkTeamModal.openRecordModal}
          provider={addWorkTeamModal.record}
        />
      )}
      {showWorkTeamModal.record && (
        <WorkTeamsShowDialog
          onClose={showWorkTeamModal.handleCloseRecordModal}
          open={showWorkTeamModal.openRecordModal}
          workTeam={showWorkTeamModal.record}
        />
      )}
      {addWorkTeamUsersModal.record && (
        <WorkTeamUsersAddDialog
          onClose={addWorkTeamUsersModal.handleCloseRecordModal}
          open={addWorkTeamUsersModal.openRecordModal}
          provider={addWorkTeamUsersModal.record}
        />
      )}
    </ListPage>
  );
};
